import type { FC } from 'react';
import { useEffect, useState, useRef } from 'react';
import { Stack, Typography, CircularProgress, Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import type { FormikErrors, FormikTouched } from 'formik';
import * as Yup from 'yup';
import { keepPreviousData } from '@tanstack/react-query';
import { ClientError } from '@pflegenavi/shared-frontend/platform';
import { DynamicRecipientForm } from '../DynamicRecipientForm';
import {
  useGetRecipientSchema,
  useGetPayoutMethods,
  useValidateRecipientData,
  useGetAllCountriesPhoenix,
  useSortedCountries,
  transformFlatErrorsToNested,
  filterErrorsByTouched,
  type RecipientData,
  type RecipientSchemaParams,
} from '@pflegenavi/service-provider-frontend/api';

interface ValidationError {
  field: string;
  message: string;
}

interface ValidationErrorResponse extends ClientError {
  error: string;
  errors: ValidationError[];
}

interface MangopayConnectFormStep3Props {
  disabled: boolean;
  onSchemaLoaded?: (params: RecipientSchemaParams) => void;
  onDataChange?: (data: RecipientData) => void;
  onValidationChange?: (isValid: boolean) => void;
  recipientType: 'Individual' | 'Business';
  serviceProviderCountryId?: string;
}

// Default parameters for the recipient schema
const DEFAULT_SCHEMA_PARAMS: RecipientSchemaParams = {
  currency: 'EUR',
};

export const MangopayConnectFormStep3: FC<MangopayConnectFormStep3Props> = ({
  disabled,
  onSchemaLoaded,
  onDataChange,
  onValidationChange,
  recipientType,
  serviceProviderCountryId,
}) => {
  const { t } = useTranslation();
  const [selectedPayoutMethod, setSelectedPayoutMethod] =
    useState<string>('LocalBankTransfer');
  const [schemaParams, setSchemaParams] = useState<RecipientSchemaParams>(
    DEFAULT_SCHEMA_PARAMS
  );
  const [isAsyncValidating, setIsAsyncValidating] = useState(false);

  // Track touched state for error filtering
  const touchedRef = useRef<FormikTouched<RecipientData>>({});

  // Track unfiltered errors for validation logic (separate from display)
  const unfilteredErrorsRef = useRef<FormikErrors<RecipientData>>({});

  // Fetch available payout methods
  const {
    data: payoutMethods,
    isLoading: isLoadingPayoutMethods,
    error: payoutMethodsError,
  } = useGetPayoutMethods();

  // Set default payout method when loaded
  useEffect(() => {
    if (payoutMethods && payoutMethods.length > 0) {
      // Always prefer LocalBankTransfer if available
      const localBankTransfer = payoutMethods.find(
        (method) => method === 'LocalBankTransfer'
      );
      if (localBankTransfer) {
        setSelectedPayoutMethod('LocalBankTransfer');
      } else if (!selectedPayoutMethod) {
        // Fallback to first available method only if no method is selected
        setSelectedPayoutMethod(payoutMethods[0]);
      }
    }
  }, [payoutMethods, selectedPayoutMethod]);

  // Update schema params when payout method changes
  useEffect(() => {
    if (selectedPayoutMethod) {
      setSchemaParams({
        currency: 'EUR',
        payout_method_type: selectedPayoutMethod,
        country: serviceProviderCountryId,
      });
    }
  }, [selectedPayoutMethod, serviceProviderCountryId]);

  const {
    data: schema,
    isLoading: isLoadingSchema,
    error: schemaError,
  } = useGetRecipientSchema(schemaParams, !!selectedPayoutMethod);

  const { mutateAsync: validateRecipientData } = useValidateRecipientData();

  // Fetch countries
  const { data: countriesData, isLoading: isLoadingCountries } =
    useGetAllCountriesPhoenix({
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    });
  const countries = useSortedCountries(countriesData ?? []);

  // Extract country from schema's AllowedValues if available
  const countryField = schema?.Country;
  const defaultCountry =
    countryField &&
    typeof countryField === 'object' &&
    'AllowedValues' in countryField &&
    Array.isArray(countryField.AllowedValues) &&
    countryField.AllowedValues.length > 0
      ? countryField.AllowedValues[0]
      : '';

  // Create a separate formik instance for the dynamic recipient form
  const recipientFormik = useFormik<RecipientData>({
    initialValues: {
      DisplayName: '',
      PayoutMethodType: 'LocalBankTransfer',
      RecipientType: recipientType,
      Currency: 'EUR',
      Country: defaultCountry,
    },
    onSubmit: () => {
      // Not used, parent form handles submission
    },
    validationSchema: Yup.object(), // Disable Yup validation since we're using async validation
    validate: async (values: RecipientData) => {
      // Only validate with API if we have the required params
      if (!schemaParams || !selectedPayoutMethod) {
        return {};
      }

      setIsAsyncValidating(true);
      try {
        const response = await validateRecipientData({
          ...schemaParams,
          recipient_data: values,
        });

        // If validation passes, return no errors
        if (response.valid) {
          return {};
        }
      } catch (error: unknown) {
        // Handle validation errors from the API
        if (error instanceof ClientError) {
          const validationError = error as ValidationErrorResponse;

          if (validationError.errors && Array.isArray(validationError.errors)) {
            // First, translate all error messages
            const translatedErrors = validationError.errors.map(
              (err: ValidationError) => {
                const translationKey = `mangopay.recipient.errors.${err.message}`;
                const translatedMessage = t(translationKey);

                return {
                  field: err.field,
                  message:
                    translatedMessage !== translationKey
                      ? translatedMessage
                      : err.message,
                };
              }
            );

            // Transform flat errors to nested structure
            const nestedErrors = transformFlatErrorsToNested(translatedErrors);

            // Store unfiltered errors for validation logic
            unfilteredErrorsRef.current = nestedErrors;

            // Filter errors to only show errors for touched fields
            const filteredErrors = filterErrorsByTouched(
              nestedErrors,
              touchedRef.current
            );
            return filteredErrors;
          }
        }
      } finally {
        setIsAsyncValidating(false);
      }

      // Return empty errors if something unexpected happens
      return {};
    },
    validateOnChange: false, // Only validate on blur, not on every change
    validateOnBlur: true,
  });

  // Destructure what we need from formik to avoid re-renders
  const {
    values,
    errors,
    touched,
    isValid,
    handleChange,
    handleBlur,
    setFieldValue,
  } = recipientFormik;

  // Update touched ref whenever touched state changes
  useEffect(() => {
    touchedRef.current = touched;
  }, [touched]);

  // Update Country field when schema loads or changes
  useEffect(() => {
    const countryField = schema?.Country;
    const countryFromSchema =
      countryField &&
      typeof countryField === 'object' &&
      'AllowedValues' in countryField &&
      Array.isArray(countryField.AllowedValues) &&
      countryField.AllowedValues.length > 0
        ? countryField.AllowedValues[0]
        : undefined;
    if (countryFromSchema && values.Country !== countryFromSchema) {
      setFieldValue('Country', countryFromSchema);
    }
  }, [schema, setFieldValue, values.Country]);

  // Notify parent when schema is loaded
  useEffect(() => {
    if (schema && onSchemaLoaded && selectedPayoutMethod) {
      onSchemaLoaded(schemaParams);
    }
  }, [schema, schemaParams, onSchemaLoaded, selectedPayoutMethod]);

  // Watch form changes and notify parent
  useEffect(() => {
    if (onDataChange) {
      onDataChange(values);
    }
  }, [values, onDataChange]);

  // Watch validation state and notify parent
  useEffect(() => {
    if (onValidationChange) {
      // Check if form is valid and has been touched
      const hasErrors = Object.keys(errors).length > 0;
      const touchedCount = Object.keys(touched).length;
      const formIsValid =
        isValid && touchedCount > 0 && !hasErrors && !isAsyncValidating;
      onValidationChange(formIsValid);
    }
  }, [isValid, touched, errors, isAsyncValidating, onValidationChange]);

  // Always use new recipient flow
  if (isLoadingPayoutMethods || isLoadingSchema || isLoadingCountries) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (payoutMethodsError || schemaError) {
    return (
      <Alert severity="error">
        {t('service-provider-app.connect-account.schema-load-error')}
      </Alert>
    );
  }

  if (!payoutMethods || payoutMethods.length === 0) {
    return (
      <Alert severity="warning">
        {t('service-provider-app.connect-account.no-payout-methods')}
      </Alert>
    );
  }

  return (
    <Stack gap={2}>
      <Typography color="grey.500">
        {t('service-provider-app.connect-account.bank-account-details')}
      </Typography>

      {/* Show schema-based form when loaded */}
      {schema && selectedPayoutMethod && (
        <Box component="form" onSubmit={(e) => e.preventDefault()}>
          <DynamicRecipientForm
            schema={schema}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            countryOptions={countries}
          />
        </Box>
      )}
    </Stack>
  );
};
